import * as React from "react";
import {
  WheelPicker,
  WheelPickerOption,
  WheelPickerWrapper,
} from "@ncdai/react-wheel-picker";

interface TimeSelectWheelRestrictedProps {
  onChange?: (time: string) => void;
}

export function TimeSelectWheelRestricted({
  onChange,
}: TimeSelectWheelRestrictedProps) {
  const [selectedHour, setSelectedHour] = React.useState(0);
  const [selectedMinute, setSelectedMinute] = React.useState(0);

  // Get current time and calculate available time slots (next 1 hour)
  const getTimeOptions = () => {
    const now = new Date();
    const currentHour24 = now.getHours();
    const currentMinute = now.getMinutes();

    // Calculate max time (1 hour from now)
    const maxTime = new Date(now.getTime() + 60 * 60 * 1000);
    const maxHour24 = maxTime.getHours();
    const maxMinute = maxTime.getMinutes();

    // Generate hour options
    const hourOptions: WheelPickerOption<number>[] = [];
    for (let h = currentHour24; h <= maxHour24; h++) {
      // Convert to 12-hour format for display
      let displayHour = h % 12;
      if (displayHour === 0) displayHour = 12;
      const meridiem = h >= 12 ? "PM" : "AM";

      hourOptions.push({
        label: `${displayHour.toString().padStart(2, "0")} ${meridiem}`,
        value: h,
      });
    }

    return {
      hourOptions,
      currentHour24,
      currentMinute,
      maxHour24,
      maxMinute,
    };
  };

  const timeOptions = getTimeOptions();

  // Generate minute options based on selected hour
  const getMinuteOptions = (): WheelPickerOption<number>[] => {
    const minuteOptions: WheelPickerOption<number>[] = [];

    // If selected hour is current hour
    if (selectedHour === timeOptions.currentHour24) {
      // Start from current minute
      for (let m = timeOptions.currentMinute; m < 60; m++) {
        minuteOptions.push({
          label: m.toString().padStart(2, "0"),
          value: m,
        });
      }
    }
    // If selected hour is max hour
    else if (selectedHour === timeOptions.maxHour24) {
      // End at max minute
      for (let m = 0; m <= timeOptions.maxMinute; m++) {
        minuteOptions.push({
          label: m.toString().padStart(2, "0"),
          value: m,
        });
      }
    }
    // Otherwise, all minutes are available
    else {
      for (let m = 0; m < 60; m++) {
        minuteOptions.push({
          label: m.toString().padStart(2, "0"),
          value: m,
        });
      }
    }

    return minuteOptions;
  };

  const minuteOptions = getMinuteOptions();

  // Initialize with current time
  React.useEffect(() => {
    setSelectedHour(timeOptions.currentHour24);
    setSelectedMinute(timeOptions.currentMinute);
  }, []);

  // Update parent component when time changes
  React.useEffect(() => {
    if (onChange) {
      // Convert to 12-hour format for display
      let displayHour = selectedHour % 12;
      if (displayHour === 0) displayHour = 12;
      const meridiem = selectedHour >= 12 ? "PM" : "AM";

      const formattedTime = `${displayHour
        .toString()
        .padStart(2, "0")}:${selectedMinute
        .toString()
        .padStart(2, "0")} ${meridiem}`;
      onChange(formattedTime);
    }
  }, [selectedHour, selectedMinute, onChange]);

  return (
    <div className="w-56">
      <WheelPickerWrapper>
        <WheelPicker
          options={timeOptions.hourOptions}
          defaultValue={selectedHour}
          onValueChange={(value: number) => setSelectedHour(value)}
          classNames={{
            highlightWrapper: "bg-[#1a1a1a] h-fit",
          }}
        />
        <WheelPicker
          options={minuteOptions}
          defaultValue={selectedMinute}
          onValueChange={(value: number) => setSelectedMinute(value)}
          classNames={{
            highlightWrapper: "bg-[#1a1a1a] h-fit",
          }}
        />
      </WheelPickerWrapper>
    </div>
  );
}
